import { useRef, useState } from "react";
import { BrandingConfig, ClockOverlayConfig, LogoCorner, PlaylistSettings } from "@shared/types";
import { ApiError, mediaUrl, MediaUploadResult } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SettingsPanelProps {
  settings: PlaylistSettings;
  onChange: (settings: PlaylistSettings) => void;
  /** Upload a logo image (workspace media). Branding logo upload is hidden if absent. */
  uploadMedia?: (file: File) => Promise<MediaUploadResult>;
}

export function SettingsPanel({ settings, onChange, uploadMedia }: SettingsPanelProps) {
  const set = <K extends keyof PlaylistSettings>(key: K, value: PlaylistSettings[K]) =>
    onChange({ ...settings, [key]: value });

  const overlay = settings.clockOverlay;
  const setOverlay = <K extends keyof ClockOverlayConfig>(key: K, value: ClockOverlayConfig[K]) =>
    onChange({ ...settings, clockOverlay: { ...overlay, [key]: value } });

  const branding = settings.branding;
  const setBrand = <K extends keyof BrandingConfig>(key: K, value: BrandingConfig[K]) =>
    onChange({ ...settings, branding: { ...branding, [key]: value } });

  const logoInput = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  async function onLogoSelected(file: File | undefined) {
    if (!file || !uploadMedia) return;
    setUploadingLogo(true);
    setLogoError(null);
    try {
      const res = await uploadMedia(file);
      setBrand("logoKey", res.key);
    } catch (err) {
      setLogoError(err instanceof ApiError ? err.message : "Logo upload failed");
    } finally {
      setUploadingLogo(false);
      if (logoInput.current) logoInput.current.value = "";
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Playback settings</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="idle">Idle takeover (seconds)</Label>
            <Input
              id="idle"
              type="number"
              min={5}
              max={3600}
              value={settings.idleTimeoutSec}
              onChange={(e) => set("idleTimeoutSec", Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">
              How long without interaction before the kiosk chrome hides and the playlist takes
              over.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="crossfade">Crossfade (ms)</Label>
            <Input
              id="crossfade"
              type="number"
              min={0}
              max={10000}
              step={100}
              value={settings.crossfadeMs}
              onChange={(e) => set("crossfadeMs", Number(e.target.value))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="default-duration">Default slide duration (seconds)</Label>
            <Input
              id="default-duration"
              type="number"
              min={1}
              max={3600}
              value={settings.defaultDurationSec}
              onChange={(e) => set("defaultDurationSec", Number(e.target.value))}
            />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label htmlFor="shuffle">Shuffle</Label>
              <p className="text-xs text-muted-foreground">
                Randomize order each loop. Slides sharing a <strong>Group</strong> stay together and
                in order.
              </p>
            </div>
            <Switch
              id="shuffle"
              checked={settings.shuffle}
              onCheckedChange={(v) => set("shuffle", v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Clock overlay</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label htmlFor="overlay-enabled">Show clock in the corner</Label>
              <p className="text-xs text-muted-foreground">
                A small clock (and optional weather) pinned to the bottom-right over the playlist.
              </p>
            </div>
            <Switch
              id="overlay-enabled"
              checked={overlay.enabled}
              onCheckedChange={(v) => setOverlay("enabled", v)}
            />
          </div>

          {overlay.enabled && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle label="Show date" checked={overlay.showDate} onChange={(v) => setOverlay("showDate", v)} />
              <Toggle
                label="Show seconds"
                checked={overlay.showSeconds}
                onChange={(v) => setOverlay("showSeconds", v)}
              />
              <Toggle label="24-hour time" checked={overlay.hour24} onChange={(v) => setOverlay("hour24", v)} />
              <Toggle
                label="Show weather"
                checked={overlay.showWeather}
                onChange={(v) => setOverlay("showWeather", v)}
              />
              {overlay.showWeather && (
                <>
                  <div className="space-y-2">
                    <Label>Weather location (city)</Label>
                    <Input
                      value={overlay.locationName ?? ""}
                      onChange={(e) => setOverlay("locationName", e.target.value)}
                      placeholder="Honolulu"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Temperature unit</Label>
                    <Select
                      value={overlay.tempUnit}
                      onValueChange={(v) => setOverlay("tempUnit", v as "F" | "C")}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="F">Fahrenheit (°F)</SelectItem>
                        <SelectItem value="C">Celsius (°C)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label>Clinic logo</Label>
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-28 items-center justify-center overflow-hidden rounded-md border bg-muted/40">
                {branding.logoKey ? (
                  <img src={mediaUrl(branding.logoKey)} alt="Logo" className="max-h-full max-w-full" />
                ) : (
                  <span className="text-xs text-muted-foreground">No logo</span>
                )}
              </div>
              {uploadMedia && (
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploadingLogo}
                    onClick={() => logoInput.current?.click()}
                  >
                    {uploadingLogo ? "Uploading…" : branding.logoKey ? "Replace logo" : "Upload logo"}
                  </Button>
                  {branding.logoKey && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setBrand("logoKey", undefined)}
                    >
                      Remove
                    </Button>
                  )}
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void onLogoSelected(e.target.files?.[0])}
                  />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">A transparent PNG looks best.</p>
            {logoError && <p className="text-sm text-destructive">{logoError}</p>}
          </div>

          {branding.logoKey && (
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Logo corner</Label>
                <Select
                  value={branding.logoCorner}
                  onValueChange={(v) => setBrand("logoCorner", v as LogoCorner)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top-left">Top left</SelectItem>
                    <SelectItem value="top-right">Top right</SelectItem>
                    <SelectItem value="bottom-left">Bottom left</SelectItem>
                    <SelectItem value="bottom-right">Bottom right</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Size ({branding.logoSizePct}% of height)</Label>
                <Slider
                  min={3}
                  max={40}
                  step={1}
                  value={[branding.logoSizePct]}
                  onValueChange={([v]) => setBrand("logoSizePct", v)}
                />
              </div>
              <div className="space-y-2">
                <Label>Opacity ({branding.logoOpacity}%)</Label>
                <Slider
                  min={10}
                  max={100}
                  step={5}
                  value={[branding.logoOpacity]}
                  onValueChange={([v]) => setBrand("logoOpacity", v)}
                />
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Brand background</Label>
              <Input
                type="color"
                className="h-9 w-full p-1"
                value={branding.brandBgColor}
                onChange={(e) => setBrand("brandBgColor", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Brand text color</Label>
              <Input
                type="color"
                className="h-9 w-full p-1"
                value={branding.brandTextColor}
                onChange={(e) => setBrand("brandTextColor", e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <Label>Use brand colors for new text slides</Label>
              <p className="text-xs text-muted-foreground">
                New text slides start with your brand background/text colors.
              </p>
            </div>
            <Switch
              checked={branding.useBrandTextColors}
              onCheckedChange={(v) => setBrand("useBrandTextColors", v)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border p-3">
      <Label className="text-sm">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
